import React from 'react';
import { Modal, Alert, List, Typography } from 'antd';
import { WarningOutlined, CloseCircleOutlined } from '@ant-design/icons';

const { Text, Title } = Typography;

/**
 * ShiftAssignmentValidation Component
 *
 * Reusable modal to display shift assignment conflicts and warnings
 *
 * Props:
 * - visible: boolean - Controls modal visibility
 * - conflicts: array - Array of error/conflict strings
 * - onClose: function - Callback when modal is closed
 * - title: string - Optional custom title (default: "Assignment Conflicts Detected")
 * - type: string - 'error' or 'warning' (default: 'error')
 */
function ShiftAssignmentValidation({
  visible,
  conflicts = [],
  onClose,
  title = 'Assignment Conflicts Detected',
  type = 'error'
}) {

  const getConflictIcon = (conflictText) => {
    // Determine icon based on conflict type
    if (conflictText.toLowerCase().includes('overlap')) {
      return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
    } else if (conflictText.toLowerCase().includes('rest period')) {
      return <WarningOutlined style={{ color: '#fa8c16' }} />;
    } else if (conflictText.toLowerCase().includes('unavailable')) {
      return <CloseCircleOutlined style={{ color: '#ff7875' }} />;
    } else {
      return <WarningOutlined style={{ color: '#faad14' }} />;
    }
  };

  const getConflictColor = (conflictText) => {
    if (conflictText.toLowerCase().includes('overlap')) {
      return '#fff1f0';
    } else if (conflictText.toLowerCase().includes('rest period')) {
      return '#fff7e6';
    } else if (conflictText.toLowerCase().includes('unavailable')) {
      return '#fff1f0';
    } else {
      return '#fffbe6';
    }
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {type === 'error' ? (
            <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 20 }} />
          ) : (
            <WarningOutlined style={{ color: '#faad14', fontSize: 20 }} />
          )}
          <Title level={4} style={{ margin: 0 }}>
            {title}
          </Title>
        </div>
      }
      open={visible}
      onCancel={onClose}
      onOk={onClose}
      okText="Close"
      cancelButtonProps={{ style: { display: 'none' } }}
      width={600}
      centered
    >
      <div style={{ marginTop: 16 }}>
        <Alert
          message="Cannot assign employee to this shift"
          description="The following conflicts were detected. Please resolve them before proceeding with the assignment."
          type={type}
          showIcon
          style={{ marginBottom: 16 }}
        />

        <List
          dataSource={conflicts}
          renderItem={(conflict, index) => (
            <List.Item
              style={{
                padding: '12px 16px',
                backgroundColor: getConflictColor(conflict),
                borderRadius: 4,
                marginBottom: 8,
                border: '1px solid #f0f0f0'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
                <div style={{ marginRight: 12, marginTop: 2 }}>
                  {getConflictIcon(conflict)}
                </div>
                <div style={{ flex: 1 }}>
                  <Text strong style={{ fontSize: 14 }}>
                    Conflict {index + 1}:
                  </Text>
                  <br />
                  <Text style={{ fontSize: 13, color: '#595959' }}>
                    {conflict}
                  </Text>
                </div>
              </div>
            </List.Item>
          )}
          style={{ maxHeight: 400, overflowY: 'auto' }}
        />

        {conflicts.length === 0 && (
          <div style={{ textAlign: 'center', padding: 20, color: '#999' }}>
            No conflicts detected.
          </div>
        )}

        <div style={{ marginTop: 16, padding: 12, backgroundColor: '#f0f5ff', borderRadius: 4 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            <strong>Tip:</strong> You can manage employee availability and preferences from the
            Shift Management section to avoid future conflicts.
          </Text>
        </div>
      </div>
    </Modal>
  );
}

export default ShiftAssignmentValidation;
